import { forwardRef, type HTMLProps } from "react";
import {
  useMergeRefs,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import { useDesignTokens } from "@boi/react-design-tokens";
import { CloseIcon } from "@boi/react-icons";

import * as S from "./TooltipContent.styled";
import { useTooltipContext } from "../Tooltip";
import { useOverlay } from "../../OverlayContext";
import { Typography } from "../../Typography";

const TooltipContent = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(
  (props, ref) => {
    const { children, ...otherProps } = props;

    const { tokens } = useDesignTokens();
    const { tooltip } = tokens;

    const { portalElement } = useOverlay();

    const {
      open,
      setOpen,
      isControlled,
      maxWidth,
      arrowRef,
      floatingData,
      interactionsData,
    } = useTooltipContext();

    const mergedRefs = useMergeRefs([floatingData.refs.setFloating, ref]);

    if (!open) return null;

    return (
      <FloatingPortal root={portalElement}>
        <FloatingFocusManager
          context={floatingData.context}
          disabled={!isControlled}
        >
          <S.Container
            ref={mergedRefs}
            style={floatingData.floatingStyles}
            {...interactionsData.getFloatingProps(otherProps)}
            $isControlled={isControlled}
            $maxWidth={maxWidth}
            role={isControlled ? "dialog" : "tooltip"}
          >
            <S.Arrow
              ref={arrowRef}
              context={floatingData.context}
              fill={tooltip.backgroundColor}
              width={parseInt(tooltip.arrow.width)}
              height={parseInt(tooltip.arrow.height)}
            />

            <Typography
              color={tooltip.color}
              variant={tooltip.typography.variant}
              fontFamily={tooltip.typography.fontFamily}
            >
              {children}
            </Typography>

            {isControlled && (
              <S.CloseButton
                onPress={() => setOpen(false)}
                aria-label="Close tooltip"
              >
                <CloseIcon />
              </S.CloseButton>
            )}
          </S.Container>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }
);

TooltipContent.displayName = "TooltipContent";

export default TooltipContent;
